import { computed, ComputedRef, inject, ref, watch } from 'vue'
import { routeLocationKey } from 'vue-router'
import { useStore } from '@web/store'
import { specialProjectId } from '@web/common/ExternalConfig/getters'
import { isEmbedded } from '@web/composition/useEmbedMode'
import PAGE_NAME from '@web/consts/PAGE_NAME'
import {
  hasConsent,
  acceptConsent,
  migrateGuestConsent
} from '@web/platform.v3/composables/cookieConsentStorage'

export interface UseCookieConsentDeps {
  getUserId?: () => number | string | null | undefined
  // specialProjectId() типизирован как 'any' | 'take', но на проектах, где бэк
  // projectId не отдаёт (например iqface), фактически возвращает undefined.
  getProjectId?: () => 'any' | 'take' | undefined
  getRouteName?: () => unknown
  isEmbedded?: () => boolean
}

export interface UseCookieConsent {
  isVisible: ComputedRef<boolean>
  accept: () => void
}

// Ревизия для пересчёта isVisible после записи в хранилище: сами cookie и
// localStorage не реактивны. Модульная, а не локальная — композабл вызывается
// из нескольких мест (CookieConsent.vue и плашка оплаты), и accept() в одном
// инстансе обязан поднять isVisible у всех остальных, а не только у своего.
// Тот же паттерн, что у dismissTick и expanded в usePaymentFailureWarning.ts.
const revision = ref(0)

export function useCookieConsent (deps: UseCookieConsentDeps = {}): UseCookieConsent {
  // useStore() можно вызывать только внутри setup(). Если зависимость передана,
  // store не нужен — не трогаем его вовсе (паттерн usePersonalSelection).
  const store = deps.getUserId ? null : useStore()

  const getUserId = deps.getUserId ?? ((): number | string | null | undefined => store?.getters['user/user']?.id)
  const getProjectId = deps.getProjectId ?? specialProjectId
  // Маршрут берём inject'ом с дефолтом, а не useRoute(): композаблом
  // пользуется и плашка оплаты, которую тесты монтируют без роутера, —
  // useRoute() там сыпал бы предупреждениями. Без роутера имени маршрута
  // нет, и плашка не показывается.
  const route = deps.getRouteName ? null : inject(routeLocationKey, null)
  const getRouteName = deps.getRouteName ?? ((): unknown => route?.name)
  const getIsEmbedded = deps.isEmbedded ?? isEmbedded

  // Плашка нужна только на Any — включаем по явному projectId === 'any'.
  // На Take и на проектах без projectId (iqface) не показываем.
  // projectId приходит из внешнего конфига, который в main.ts загружается
  // до mount() — значит при первом рендере значение уже верное, мигания нет.
  const isEnabledForProject = computed<boolean>(() => getProjectId() === 'any')

  // MOB-534: плашка — только на Главной и никогда во встроенном режиме.
  // В WebView/iframe за согласие отвечает контейнер: плашка перекрывала бы
  // контент, ради которого страницу открыли. Остальные страницы ЛК — по
  // решению заказчика. Условие в композабле, а не в CookieConsent.vue: плашка оплаты
  // поднимается над куки по этому же isVisible и должна видеть то же самое.
  const isAllowedHere = computed<boolean>(() => !getIsEmbedded() && getRouteName() === PAGE_NAME.HOME)

  const userId = computed<number | string | null | undefined>(() => getUserId())

  const isVisible = computed<boolean>(() => {
    if (!isEnabledForProject.value || !isAllowedHere.value) {
      return false
    }
    // Читаем revision, чтобы computed зависел от неё и пересчитывался после
    // записи в cookie/localStorage (сами по себе они не реактивны).
    const trackedRevision = revision.value
    return trackedRevision >= 0 && !hasConsent(userId.value)
  })

  function accept (): void {
    if (!isEnabledForProject.value) {
      return
    }
    acceptConsent(userId.value)
    revision.value += 1
  }

  // Пользователь авторизовался — переносим гостевое согласие на его ключ.
  watch(userId, (value) => {
    if (!value || !isEnabledForProject.value) {
      return
    }
    migrateGuestConsent(value)
    revision.value += 1
  }, { immediate: true })

  return { isVisible, accept }
}
